import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import { useEffect, useRef } from "react";

/**
 * Hook to detect when a component unmounts or when its active state changes from true to false.
 * This can be useful to detect implicit deactivations that are caused by an unmount of the component.
 * E.g. when a tool is deactivated explicity by switching off by a "tool active" flag and in other cases
 * implicitly by switching to another view.
 *
 * @param isActive Whether the tool/component using this hook is active.
 * @param onUnmountOrDeactivation Callback triggered when the component unmounts or when isActive changes from true to false.
 */
export function useDetectUnmountOrDeactivation(
  isActive: boolean,
  onUnmountOrDeactivation: () => void,
): void {
  // This function is reactive state that is passed into the non-exhaustive effect below.
  // This however conflicts with its goal of detecting unmounts of a component, as the effect cleanup uses the values it received during
  // mount. So when the effect cleanup runs, these function values can be outdated. To work around this conflict, the functions are stored
  // in refs, that are used on-demand.
  const onUnmountOrDeactivationRef = useRef(onUnmountOrDeactivation);
  useEffect(() => {
    onUnmountOrDeactivationRef.current = onUnmountOrDeactivation;
  }, [onUnmountOrDeactivation]);

  // Should call the callback, whenever the component unmounts or the activation flag changes from true to false.
  // Using non-exhaustive effect to avoid triggering the effect on other occurrences.
  useNonExhaustiveEffect(() => {
    if (isActive) {
      return () => {
        onUnmountOrDeactivationRef.current();
      };
    }
  }, [isActive]);
}
